/*
 * "What bra size do you usually wear?" on bra product pages (snippets/invisibra-size-finder.liquid).
 *
 * Band + cup -> InvisiSizing.forBraSize() -> the matching size button in the theme's variant picker is
 * clicked (so the theme fetches the variant as if the shopper had tapped it) and tagged "Yours". The
 * variant picker is re-rendered on every variant change, so the tag is put back after each one.
 *
 * The bra size is remembered in localStorage ('ib-usual-size'), so the next bra opens on the shopper's
 * size. Copy lives in the snippet's <template> elements, not here.
 */
(() => {
  const S = window.InvisiSizing;
  if (!S || customElements.get('invisibra-size-finder')) return;

  const STORAGE_KEY = 'ib-usual-size';

  const remember = (value) => {
    try {
      if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
      else localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // Private mode or blocked storage: the finder still works, it just won't remember.
    }
  };

  const recall = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    } catch (e) {
      return null;
    }
  };

  class InvisibraSizeFinder extends HTMLElement {
    connectedCallback() {
      this.data = S.fromPage(this);
      if (!this.data) return;
      this.section = this.closest('.shopify-section') || document;
      this.sold = (this.dataset.sold || '').split(',').filter(Boolean);
      this.band = this.querySelector('[data-finder-band]');
      this.cup = this.querySelector('[data-finder-cup]');
      this.result = this.querySelector('[data-finder-result]');
      this.yours = null;

      const onChange = () => this.update({ fromShopper: true });
      this.band.addEventListener('change', onChange);
      this.cup.addEventListener('change', onChange);
      this.section.addEventListener('on:variant:change', () => this.tagYours());

      const saved = recall();
      if (saved && this.hasOption(this.band, saved.band) && this.hasOption(this.cup, saved.cup)) {
        this.band.value = saved.band;
        this.cup.value = saved.cup;
        // Only pick it for them over the size the page opened on (not on a ?variant= link, or a size they chose).
        this.update({ fromShopper: false, select: !this.chosenSize() });
      }
    }

    hasOption(select, value) {
      return value !== undefined && Array.from(select.options).some((o) => o.value === String(value) && o.value !== '');
    }

    fieldset() {
      const name = this.dataset.option;
      return Array.from(this.section.querySelectorAll('variant-picker .option-selector')).find(
        (el) => el.dataset.option === name,
      );
    }

    // The page opens on the first in-stock size (select_first_variant on), so a checked size isn't always the
    // shopper's: it's theirs on a ?variant= link, or when it's not the one the page opened on (tapped before
    // this ran, or put back by the browser on Back).
    chosenSize() {
      if (new URLSearchParams(window.location.search).has('variant')) return true;
      const checked = this.fieldset()?.querySelector('.js-option:checked');
      return Boolean(checked && !checked.defaultChecked);
    }

    message(name, values) {
      const template = this.querySelector(`template[data-message="${name}"]`);
      if (!template) return '';
      return template.innerHTML.replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match));
    }

    update({ fromShopper, select = true }) {
      const band = this.band.value;
      const cupIndex = this.cup.value;
      if (band === '' || cupIndex === '') {
        this.result.innerHTML = '';
        this.yours = null;
        this.tagYours();
        if (fromShopper) remember(null);
        return;
      }
      if (fromShopper) remember({ band, cup: cupIndex });

      const r = S.forBraSize(this.data, Number(band), Number(cupIndex), this.sold);
      const values = {
        uk: r.uk,
        size: r.sizeLabel || '',
        filter: r.size ? `/collections/backlessbras?filter.v.option.size=${encodeURIComponent(r.size)}` : '',
      };

      let html = '';
      if (r.status === 'no-size') {
        html = this.message('no-size', values);
      } else if (r.status === 'not-sold') {
        html = this.message('not-sold', values);
      } else {
        html = this.message(fromShopper ? 'ok' : 'remembered', values);
        if (r.between) html += this.message('between', { lower: r.between.lowerLabel, upper: r.between.upperLabel });
      }
      if (r.aboveLimit && r.status !== 'no-size') html += this.message('above-limit', values);
      this.result.innerHTML = html;
      this.result.querySelectorAll('a[data-href]').forEach((a) => {
        a.href = a.dataset.href;
        a.removeAttribute('data-href');
      });

      this.yours = r.status === 'ok' ? r.size : null;
      this.tagYours();
      if (this.yours && select) this.pick(this.yours);
    }

    pick(size) {
      // The variant picker's script loads after this one; a click before it's listening would check the
      // button without fetching the variant.
      customElements.whenDefined('variant-picker').then(() => {
        setTimeout(() => {
          const input = Array.from(this.fieldset()?.querySelectorAll('.js-option') || []).find((el) => el.value === size);
          if (input && !input.checked) input.click();
        }, 0);
      });
    }

    tagYours() {
      const fieldset = this.fieldset();
      if (!fieldset) return;
      fieldset.querySelectorAll('.js-option').forEach((input) => {
        const label = fieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (label) label.classList.toggle('ib-yours', input.value === this.yours);
      });
    }
  }

  customElements.define('invisibra-size-finder', InvisibraSizeFinder);
})();
